
import { defineComponent, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { message } from 'ant-design-vue';
import axios, { AxiosError } from 'axios';

import { useStore } from '@/store';
import { amplitudeInstance } from '@/analytics';
import { addToFavorite } from '@/api/plant';
import { animateAddToMyPlants } from '@/helper/utils';

import { ApiPlantResponse } from '@/@types/plant';
import { MODALS } from '@/@types/base';
import { ServerError } from '@/@types/base';

import BaseIcon from '@/components/BaseIcon.vue';

function isHtmlElement(target: unknown): target is HTMLElement {
  return target instanceof HTMLElement;
}

function isAxiosError(error: unknown): error is AxiosError<ServerError> {
  return axios.isAxiosError(error) && error.response !== undefined;
}

export default defineComponent({
  name: 'PlantCard',

  components: {
    BaseIcon,
  },

  props: {
    plant: {
      type: Object as () => ApiPlantResponse,
      required: true,
    },
    rect: {
      type: Boolean,
      default: false,
    },
    amplitudeKey: {
      type: String,
      default: null,
    },
  },

  setup(props) {
    const { t } = useI18n();
    const store = useStore();

    const isFetch = ref<boolean>(false);

    const userEmpty = computed<boolean>(() => {
      return store.getters['auth/userEmpty'];
    });

    const ids = computed<number[]>(
      () => store.getters['my-plants/myPlantsIds']
    );

    const isInMyPlants = computed<boolean>(() => {
      return ids.value.includes(props.plant.id);
    });

    const addToMyPlants = async function addPlantToMyCollection(
      e: Event
    ): Promise<void> {
      if (userEmpty.value) {
        return store.dispatch('base/openModal', MODALS.AUTHORIZATION);
      }

      if (ids.value.length === 2) {
        return store.dispatch('base/openModal', MODALS.SUBSCRIPTIONS_MODAL);
      }

      isFetch.value = true;

      try {
        const newPlant = await addToFavorite(props.plant.id);

        if (e.target && isHtmlElement(e.target)) {
          animateAddToMyPlants(e.target);
        }

        store.dispatch('my-plants/addItemToMyPlants', newPlant);
        store.dispatch('my-plants/increaseMyNewPlantsCount', null);
        store.dispatch('plant/updatePrefetchedPlant', newPlant);

        message.success(t('message_favorites_add_success'));

        amplitudeInstance.logEvent('Plant_added');
      } catch (error) {
        if (isAxiosError(error) && error.response?.status !== 403) {
          message.error(t('message_oops'));
        }
      }

      isFetch.value = false;
    };

    const logAmplitudeEvent = function logAnalytics() {
      if (!props.amplitudeKey) return;

      amplitudeInstance.logEvent('Plant_description_page_shown', {
        referrer: props.amplitudeKey,
      });
    };

    const prefetchPlantData = function getPlantDataAndSaveToStore() {
      store.dispatch('plant/getPrefetchedPlantData', props.plant.id);
    };

    return {
      t,
      isFetch,
      isInMyPlants,
      addToMyPlants,
      logAmplitudeEvent,
      prefetchPlantData,
    };
  },
});
